import { watch } from 'vue';
import { useStore } from '@/store'; 
import { storeToRefs } from 'pinia';
import { getMedia } from "@/util"; 
import { createPin, initPinSystem } from './pin';

// BabylonJS Imports
import { Vector3, Color3 } from "@babylonjs/core/Maths/math";
import { Animation } from "@babylonjs/core/Animations/animation";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Texture } from "@babylonjs/core/Materials/Textures/texture"
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial'
import { ActionManager } from "@babylonjs/core/Actions/actionManager";
import { ExecuteCodeAction } from '@babylonjs/core/Actions/directActions';
import { CubicEase, EasingFunction } from "@babylonjs/core/Animations/easing";
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import "@babylonjs/loaders/glTF/2.0/"; 
import "@babylonjs/core/Materials/Textures/Loaders/envTextureLoader";
import "@babylonjs/core/Animations/animatable"
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';

// Local Module State
let areas: VersionDataArea[] = [];
let activeArea: VersionDataArea | null = null;
const lightmaps = new Map<string, {lightmapTexture: Texture | null, lightmapMaterialName: string}>();
let isDragging = false; // Logic flag, no need for reactivity

const ease = new CubicEase();
ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

/**
 * Main Entry Point. Call this from your Vue Component onMounted.
 */
export const initializeWorld = async () => {
    const store = useStore();
    const { 
        versionData, 
        activeAreaID, 
        isWaterEnabled, 
        isSewageEnabled, 
        isGasEnabled
    } = storeToRefs(store);

    // 1. Setup Watchers (linked to Pinia Store)
    watch(activeAreaID, (newID) => {
        if (newID != null) {
            console.log("Active Area: " + newID);
            activeArea = areas[newID];
            setAreaActive(activeArea, true);
            triggerGroundAnimation(activeArea.nodeName, true);
        } else {
            console.log("No active Area -> Default View");
            setAreaActive(activeArea, false);
            if(activeArea) triggerGroundAnimation(activeArea.nodeName, false);
            activeArea = null;
        }
    });

    watch(isWaterEnabled, (val) => {
        enableCategory("Wasser", val ? 1 : 0);
        adjustLightMaps();
    });

    watch(isSewageEnabled, (val) => {
        enableCategory("Abwasser", val ? 1 : 0);
        adjustLightMaps();
    });

    watch(isGasEnabled, (val) => {
        enableCategory("Gas", val ? 1 : 0);
        adjustLightMaps();
    });

    // 2. Logic Initialization
    if (!versionData.value) {
        console.error("VersionData is missing in Store!");
        return;
    }

    // Get versionData from versions.json
    areas = versionData.value.areas;

    // Create TransformNode for Pins
    var allPinsNode = new TransformNode("Pins");

    // Create Pin-TransformNode for main view
    var mainPinsNode = new TransformNode("Pins_Orbit");
    mainPinsNode.parent = allPinsNode;

    // Create Pin-TransformNode for each Area
    versionData.value.areas.forEach((area) => {
        var areaPinsNode = new TransformNode("Pins_" + area.nodeName);
        areaPinsNode.parent = allPinsNode;
    });

    initEventlisteners(); // Interaction (zoom, mouse move etc.)
    loadAssets();         // Babylon Assets Manager

    // Create Pins
    initPinSystem();
    areas.forEach((area, index) => {
        createPin({
            id: '0-' + index, 
            position: area.mainPin.position, 
            alpha: area.mainPin.alpha, 
            beta: area.mainPin.beta, 
            target: area.mainPin.target, 
            area: null
        });
        
        area.pins.forEach((pin, i) => {
            createPin({
                id: (Number(index) + 1) + '-' + i, 
                position: pin.position, 
                alpha: pin.alpha, 
                beta: pin.beta, 
                target: pin.target, 
                area: area
            });
        });
    });
};

const loadAssets = async () => {
    const store = useStore();
    const { versionData, availableSegments } = storeToRefs(store);

    if(!versionData.value) return;

    // Load Island
    let meshTask = window.assetsManager.addMeshTask("", "", "", versionData.value.mesh);
    meshTask.onSuccess = (task) => {
        task.loadedMeshes[0].name = "AliaxisWorld_Root";

        cloneMaterials();
        addHoverEffect();

        // Segments Setup
        if(availableSegments.value) {
            store.setWaterBool(availableSegments.value.water);
            store.setGasBool(availableSegments.value.gas);
            store.setSewageBool(availableSegments.value.sewage);
        }

        // Disable Targets for Areas
        areas.forEach(area => {
            setAreaActive(area, false);
        });
    }
    meshTask.onError = () => {
        console.error("Error loading File.");
    }

    // Skybox / Environment
    let skyboxTask = window.assetsManager.addCubeTextureTask("", getMedia(versionData.value.env));
    skyboxTask.onSuccess = (task) => { 
        window.scene.environmentTexture = task.texture;
        window.scene.environmentIntensity = 1.8;
    }

    // Lightmaps
    lightmaps.clear();
    console.log("-- Loading Lightmaps --")
    versionData.value.lightmaps.forEach((lightmap) => {
        let lightMapTask = window.assetsManager.addTextureTask("", lightmap.url, false, false, Texture.LINEAR_LINEAR_MIPLINEAR);
        lightMapTask.onSuccess = (task) => {
            let lightmapName = lightmap.tags.sort().join("_");
            let lightmapMaterialName = lightmap.material;
            let lightmapTexture = task.texture;
            lightmapTexture.name = "Lightmap_" + lightmapName;
            lightmapTexture.hasAlpha = true;
            lightmaps.set(lightmapName, {lightmapTexture: lightmapTexture, lightmapMaterialName: lightmapMaterialName});
        }   
    })

    // Add Clouds/Birds
    let environmentNode = new TransformNode("Environment");
    versionData.value.environment.forEach((environment) => {
        createTexturePlane(new Vector3(environment.position.x, environment.position.y, environment.position.z), environment.texture, environment.size, environmentNode);
    });
    
    // Load assets
    window.assetsManager.load();
};

const adjustLightMaps = () => {
    const store = useStore();
    const { isWaterEnabled, isSewageEnabled, isGasEnabled, versionData } = storeToRefs(store);

    // Find correct Lightmap for Ground
    var targetTextureArray: string[] = [];

    if(isWaterEnabled.value) targetTextureArray.push("water");
    if(isSewageEnabled.value) targetTextureArray.push("sewage");
    if(isGasEnabled.value) targetTextureArray.push("gas");
    if(targetTextureArray.length == 0) targetTextureArray.push("empty");

    targetTextureArray.sort();

    let targetLightmapName = targetTextureArray.join("_");
    let lightmap_ground = lightmaps.get(targetLightmapName);

    // Set Lightmap for Ground
    if(lightmap_ground) {
        let material = window.scene.getMaterialByName(lightmap_ground.lightmapMaterialName) as PBRMaterial;
        if(material && lightmap_ground.lightmapTexture)
            material.albedoTexture = lightmap_ground.lightmapTexture;
    }
    
    // Set SPECIAL Lightmaps for Houses in Version 1.0.0 
    if(versionData.value && versionData.value.id == "1.0.0"){
        let h3 = lightmaps.get('house3');
        if(h3) {
            var lightmap_house3 = {
                material: h3.lightmapMaterialName, 
                texture: !isSewageEnabled.value ? h3.lightmapTexture : window.scene.getTextureByName("Haus_3 (Base Color)")
            }
            const mat = window.scene.getMaterialByName(lightmap_house3.material) as PBRMaterial;
            if(mat) mat.albedoTexture = lightmap_house3.texture;
        }
    }
};

const enableCategory = (name: string, visibility: number) => {
    const node = window.scene.getNodeByName(name);
    if(node) node.setEnabled(visibility != 0);

    let char = (name == "Abwasser" ? "a" : name == "Wasser" ? "w" : "g");
    var target1Meshes = window.scene.getNodeByName("Ground_1") ? (window.scene.getNodeByName("Ground_1") as TransformNode).getChildMeshes() : [];
    var target2Meshes = window.scene.getNodeByName("Ground_2") ? (window.scene.getNodeByName("Ground_2") as TransformNode).getChildMeshes() : [];

    target1Meshes.concat(target2Meshes).forEach(child => {
        if(child.name.charAt(0) == char)
            child.setEnabled(visibility != 0)
    });
};

const createTexturePlane = (position: Vector3, url: string, size: number, environmentNode: TransformNode) => {
    let plane = MeshBuilder.CreatePlane(url, {size: size}, window.scene);
    plane.position = position;
    plane.parent = environmentNode;
    plane.billboardMode = Mesh.BILLBOARDMODE_ALL;
    
    const mat = new StandardMaterial("TexturePlaneMaterial_" + url, window.scene);
    mat.opacityTexture = mat.diffuseTexture = new Texture(getMedia("textures/" + url), window.scene);
    mat.emissiveColor = new Color3(1,1,1);
    mat.disableLighting = true;
    
    plane.material = mat;
    plane.visibility = 0.8;
    plane.isPickable = false;
};

const cloneMaterials = () => {
    // Clone Materials for each mesh to stop sharing materials.
    let meshes: AbstractMesh[] = [];
    const getDescendants = (name: string) => {
        const node = window.scene.getNodeByName(name);
        return node ? node.getDescendants(false) as AbstractMesh[] : [];
    }

    meshes = meshes.concat(getDescendants("Wasser"));
    meshes = meshes.concat(getDescendants("Gas"));
    meshes = meshes.concat(getDescendants("Abwasser"));
    meshes = meshes.concat(getDescendants("Friamat"));
    meshes = meshes.concat(getDescendants("iPhone"));
    meshes = meshes.concat(getDescendants("StopClock"));

    meshes.forEach(mesh => {
        if(mesh.material && mesh.getClassName() == "Mesh")
            mesh.material = mesh.material.clone(mesh.name + "_" + mesh.material.name + "_cloneForAnimation");
    });
};

const addHoverEffect = () => {
    const store = useStore();
    const { products } = storeToRefs(store);

    let assignments = [ 
        // Sewage Segment
        {mesh: "A1", productID: 1000},
        {mesh: "A2", productID: 1001},
        {mesh: "A3_Grau", productID: 1002},
        {mesh: "A3_Orange", productID: 1017},
        {mesh: "A4", productID: 1003},
        {mesh: "A6", productID: 1005},
        {mesh: "A7", productID: 1006},
        {mesh: "A8", productID: 1007},
        {mesh: "4", productID: 1008}, 
        {mesh: "EasyclipCity", productID: 1009},
        {mesh: "Bend45ss", productID: 1010},
        {mesh: "Branch45DS", productID: 1011},
        {mesh: "AccessPlug", productID: 1012},
        {mesh: "NonReturnValveClassica", productID: 1013},
        {mesh: "AccessJunctionBasis", productID: 1014},
        {mesh: "TrapInterceptor", productID: 1015},
        {mesh: "InvertReducer", productID: 1016},
        {mesh: "NonReturnValve", productID: 1018},
        {mesh: "PPTrappedRainwaterGulli", productID: 1019},
        {mesh: "Ueberschiebmuffe", productID: 1020},

        // Water Segment
        {mesh: "W1", productID: 1100},
        {mesh: "MB-SDR11", productID: 1101},
        {mesh: "W3", productID: 1102},
        {mesh: "W4", productID: 1103},
        {mesh: "FBS", productID: 1104},
        {mesh: "W6", productID: 1105},
        {mesh: "W7", productID: 1106},
        {mesh: "DAV", productID: 1107},
        {mesh: "DBS", productID: 1108},
        {mesh: "W10", productID: 1109},
        {mesh: "Friagrip", productID: 1110}, 
        {mesh: "FriaClamp_1", productID: 1111},
        {mesh: "FriaClamp_2", productID: 1112},
        {mesh: "Hotneck", productID: 1113},
        {mesh: "BoundaryBox", productID: 1114},
        {mesh: "Kugelhahn", productID: 1115},
        {mesh: "Magnum", productID: 1116},
        {mesh: "PUKX", productID: 1117},

        // Gas Segment
        {mesh: "G1", productID: 1200},
        {mesh: "G2", productID: 1201},
        {mesh: "G3", productID: 1202},
        {mesh: "G4", productID: 1203},
        {mesh: "G5", productID: 1204},
        {mesh: "KBS", productID: 1205},
        {mesh: "GAB", productID: 1206},
        {mesh: "G8", productID: 1207},
        {mesh: "G9", productID: 1208},
        {mesh: "DAA", productID: 1209},
        {mesh: "G11", productID: 1210},
        {mesh: "G12", productID: 1211},
        {mesh: "G13", productID: 1212},
        {mesh: "G14", productID: 1213},
        {mesh: "W7_Gas", productID: 1214},

        // Extras
        {mesh: "Friamat", productID: 1300},
        {mesh: "iPhone", productID: 1301},
        {mesh: "StopClock", productID: 1302},
    ]

    console.groupCollapsed("-- Activate Products --");
    products.value.forEach((product) => {
        console.log("%cProduct(ID: " + product.DO_NOT_CHANGE_ID + "):" + product.data[0].title, "color: green; font-weight: bold; font-style: italic");
        
        // Check if product has a assignment
        let assignment = assignments.find(a => a.productID == product.DO_NOT_CHANGE_ID);
        if(!assignment){
            console.warn("Product: " + product.data[0].title + " with ID: " + product.DO_NOT_CHANGE_ID + " is missing an assignment, but is in products.json");
            return;
        }
        console.log("Found assignment for product: " + product.data[0].title + " (Mesh name: " + assignment.mesh + ")");

        // Check if assigned mesh is in 3D Modell
        let nodes = window.scene.getNodes().filter(node => node.name.toLowerCase().includes(assignment.mesh.toLowerCase()));
        if(nodes.length === 0){
            console.warn("Assigned Mesh to product: " + product.data[0].title + " (Mesh name: " + assignment.mesh + ") not found in AliaxisWorld.glb");
            return;
        }
        console.log("Found assigned mesh for product: " + product.data[0].title + " (Mesh name: " + assignment.mesh + ") in AliaxisWorld.glb");

        // get Target/Collider for product
        let triggerBoxName = assignment.mesh.toLowerCase() + "_target";
        let triggerMeshes = window.scene.meshes.filter((mesh) => (mesh.id ? mesh.id.toLowerCase() : '').includes(triggerBoxName));
        if(triggerMeshes.length === 0){
            console.warn("No triggerbox found for " + assignment.mesh + ", searched for triggerbox with name including: " + triggerBoxName);
            return;
        } 
        console.log("Found Triggerbox for product: " + product.data[0].title + " (Triggerbox name includes: " + triggerBoxName + ")");
        
        // Get all Meshes that belong to the assigned Node (also children) to apply hover/click effect on them
        let meshes = nodes.map(node => {
            if(node.getClassName() == "Mesh")
                return node as AbstractMesh;
            if(node.getClassName() == "TransformNode")
                return (node as TransformNode).getChildMeshes();
        }).flat().filter((mesh): mesh is AbstractMesh => mesh !== undefined); // Remove undefined values
        if(meshes.length === 0){
            console.warn("No mesh found for " + assignment.mesh + ", searched for mesh with name including: " + assignment.mesh);
            return;
        }
        console.log("Found " + meshes.length + " mesh(es) for product: " + product.data[0].title + " (Mesh name includes: " + assignment.mesh + ") to apply hover/click effect on");

        // Add Hover/Clickeffect to Mesh
        addPointerEventsToMeshes(triggerMeshes, meshes, assignment.productID);

        console.log("");
    });
    console.groupEnd();

    // Deactivate unused Triggerboxes (visibility = 0)
    console.groupCollapsed("-- Deactivate unused Triggerboxes --");
    window.scene.meshes.forEach(mesh => {
        if(mesh.name != undefined && mesh.name.includes("_target") && mesh.visibility != 0){
            console.log("TriggerBox: " + mesh.name + " exsists but has no assignment");
            mesh.visibility = 0;
        }
    });
    console.groupEnd();
};

const addPointerEventsToMeshes = (triggerMeshes: AbstractMesh[], meshes: AbstractMesh[], productID: number) => {
    const store = useStore();
    const { toolTipsDisabled, isMobile, products } = storeToRefs(store);

    triggerMeshes.forEach(triggerBox => {
        triggerBox.visibility = 0;
        triggerBox.isPickable = true;

        // OnHoverEnter: Highlight Product and show Tooltip
        triggerBox.actionManager = new ActionManager(window.scene);
        triggerBox.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOverTrigger, (event) => {
            if(!toolTipsDisabled.value && !isDragging && !isMobile.value){
                meshes.forEach(mesh => {
                    if (mesh.material)
                        Animation.CreateAndStartAnimation("hoverAlpha", mesh, "material.alpha", 60, 20, mesh.material.alpha, 0.5, Animation.ANIMATIONLOOPMODE_CONSTANT, ease);
                });
                
                // Open Tooltip
                const tooltipEl = document.getElementById("tooltip");
                if (tooltipEl) {
                    let tooltipData = {
                        top: (event.pointerY - tooltipEl.offsetHeight-35) + "px",
                        left: (event.pointerX - (tooltipEl.offsetWidth/2)) + "px",
                        productID:  productID
                    }
                    store.setTooltipData(tooltipData);
                    store.setTooltipOpen(true);
                }
            }
        }));

        // OnHoverLeave: Unhighlight Product and hide Tooltip
        triggerBox.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOutTrigger, () => {
            if(!toolTipsDisabled.value && !isDragging && !isMobile.value){
                meshes.forEach(mesh => {
                    if (mesh.material)
                        Animation.CreateAndStartAnimation("hoverOutAlpha", mesh, "material.alpha", 60, 30, mesh.material.alpha, 1, Animation.ANIMATIONLOOPMODE_CONSTANT, ease);
                });
                store.setTooltipOpen(false);
            }
        }));

        // OnClick: Open InfoPanel
        triggerBox.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPickTrigger,(event) => {
            if(isMobile.value){ // Just Open 
                    store.setTooltipOpen(false);
                    store.setSelectedProduct(products.value.find((product) => product.DO_NOT_CHANGE_ID == productID)!);      
                    store.setInfoPanelOpen(true);
            } else {
                store.setSelectedProduct(products.value.find((product) => product.DO_NOT_CHANGE_ID == productID)!);      
                store.setInfoPanelOpen(true);
            }
        }));
    });
};

const triggerGroundAnimation = (targetNodeName: string, boolean: boolean) => {
    const store = useStore();
    const { versionData } = storeToRefs(store);

    var targetNode = window.scene.getNodeByName(targetNodeName) as AbstractMesh;
    const yDelta = 2.2;
    var groundBaseValue = 0.0;
    
    if(versionData.value && versionData.value.id == "1.0.0") groundBaseValue = 0.0;
    if(versionData.value && versionData.value.id == "2.0.0") groundBaseValue = 0.05;

    // Check if targetNode exsists
    if(targetNode){
        // Animate Ground Opening/Closing
        Animation.CreateAndStartAnimation("groundY", targetNode, "position.y", 60, 80, targetNode.position.y, boolean ? targetNode.position.y-yDelta : groundBaseValue, Animation.ANIMATIONLOOPMODE_CONSTANT, ease);
        Animation.CreateAndStartAnimation("groundVis", targetNode, "visibility", 60, 80, targetNode.visibility, boolean ? 0 : 1, Animation.ANIMATIONLOOPMODE_CONSTANT, ease, () => {
            console.log("Ground: " + targetNodeName + " is " + (boolean ? "opened" : "closed"));
        });
    } else {
        console.error("TargetNode: " + targetNodeName + " not found");
    }
};

const setAreaActive = (area: VersionDataArea | null, boolean: boolean) => {
    if(!area) return;
    console.log((boolean ? "Enable" : "Disable") + " Targets for Area: " + area.nodeName);
    let areaTargetNode = window.scene.getNodeByName(area.targetNodeName);
    if(areaTargetNode) areaTargetNode.setEnabled(boolean);
};

// Add Eventlisteners for Interaction (zoom, mouse move etc.)
const initEventlisteners = () => {
    const store = useStore();
    const { isInfoPanelOpen, isTooltipOpen } = storeToRefs(store);

    // On Click on canvas close infopanel when opened. Deactivate Tooltips so user wont open them when navigating arround
    const canvas = document.getElementById("renderCanvas");
    if(canvas) {
        canvas.addEventListener("pointerdown", ()=>{
            isDragging = true;

            if(isInfoPanelOpen.value)
                store.setInfoPanelOpen(false);

            if(isTooltipOpen.value)
                store.setTooltipOpen(false);
        });

        // Activate Tooltips again when user stopped navigating
        canvas.addEventListener("pointerup", ()=>{
            isDragging = false;
        });
        
        // Zoom function
        canvas.addEventListener('wheel', (e) => {
            let camera = window.scene.activeCamera as ArcRotateCamera;
            let scale = e.deltaY;

            let i = 0;
            let intervalID = setInterval(() => {
                if(camera.fov < 1.1 && 0 < scale)
                    camera.fov += 0.005;
                if(0.75 < camera.fov && scale < 0)
                    camera.fov -= 0.005;

                i++;
                if(i == 15){
                    i = 0;
                    clearInterval(intervalID);
                }
            }, 1);
        });

        // Touch Pinch Zoom
        let lastDiff = -1;
        canvas.addEventListener('touchmove', (e) => {
            // @ts-ignore
            if (e.targetTouches.length === 2) {
                let camera = window.scene.activeCamera as ArcRotateCamera;

                // @ts-ignore
                let curDiff = Math.sqrt(
                    // @ts-ignore
                    Math.pow(e.targetTouches[0].clientX - e.targetTouches[1].clientX, 2) +
                    // @ts-ignore
                    Math.pow(e.targetTouches[0].clientY - e.targetTouches[1].clientY, 2)
                );

                if (lastDiff > 0) {
                    let zoomSensitivity = 0.002; // Anpassbar
                    
                    if (curDiff > lastDiff) {
                        if (camera.fov > 0.75) camera.fov -= zoomSensitivity * (curDiff - lastDiff);
                    } else if (curDiff < lastDiff) {
                        if (camera.fov < 1.1) camera.fov += zoomSensitivity * (lastDiff - curDiff);
                    }
                }
                lastDiff = curDiff;
            }
        }, { passive: false });

        canvas.addEventListener('touchend', () => {
            lastDiff = -1;
        });
    }
};